/**
 * RestartConfirmModal
 *
 * Host-owned restart confirmation for surfaces OUTSIDE the in-flow
 * guide (e.g. TelePortal homepage's "Restart setup" CTA). Composes
 * the shared `ConfirmModal` chassis with the `useRestartGuide`
 * mutation and the restart-flavored copy from the lifecycle registry.
 *
 * Why a separate component (vs. just opening the in-flow restart):
 *   The pre-2026-04-28 pattern was: user clicks "Retake Setup" on
 *   TelePortal homepage → `setLocation('/teleportal/setup?restart=1')`
 *   → page mounts → `<RestartUrlSignal>` sees the flag → opens the
 *   in-flow restart modal. That's three round-trips and a visible
 *   flicker just to ask "are you sure?". This component lets the
 *   host open the modal BEFORE navigating, so the confirmation
 *   happens in place.
 *
 * Used today by:
 *   • TelePortal homepage   — restart setup
 *
 * Could be used by future hosts that want a host-level restart
 * affordance (e.g. "Restart this journey" on the journey switcher).
 *
 * The in-flow Restart button (rendered by `<CoPilotGuide>` footer)
 * STILL uses the `FlowLifecycleManager`'s `controls.startRestart()`
 * — that path opens the SAME modal chassis but reads the registry
 * config inline. Same look and feel; different entry point.
 */

import { useTranslation } from '@tonnie/locale';
import { useRestartGuide } from '@tonnie/sdk';
import { useState } from 'react';

import { ConfirmModal } from './lifecycle-modals';
import { flowLifecycleRegistry } from './registry';
import type { FlowId } from './types';

interface RestartConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flowId: FlowId;
  journeyInstanceId: string | undefined;
  /**
   * Called AFTER the restart mutation succeeds. Typical use:
   * navigate the user to the assessment / setup URL where the engine
   * fresh-starts.
   */
  onAfterRestart: () => void;
}

export function RestartConfirmModal({
  open,
  onOpenChange,
  flowId,
  journeyInstanceId,
  onAfterRestart,
}: RestartConfirmModalProps) {
  const { t } = useTranslation();
  const restartMutation = useRestartGuide();
  const [submitting, setSubmitting] = useState(false);

  // Reuse the canonical restart copy from the registry — this is the
  // same `ConfirmModalConfig` the in-flow restart toolbar uses, so
  // the homepage and in-flow surfaces never drift.
  const flowConfig = flowLifecycleRegistry[flowId];
  const heroLabel = t(`copilot.lifecycle.hero.${flowId}`);

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await restartMutation.mutateAsync({
        flowId,
        journeyId: journeyInstanceId,
      });
      onOpenChange(false);
      onAfterRestart();
    } catch (err) {
      // Conflict (409) refetches via SDK on its own; non-conflict
      // errors keep the modal open and let the user retry.
      console.warn('[RestartConfirmModal] restart failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmModal
      open={open}
      config={flowConfig.restart}
      context={{ hero: heroLabel }}
      busy={submitting}
      destructive
      intent="restart"
      onConfirm={handleConfirm}
      onCancel={() => !submitting && onOpenChange(false)}
    />
  );
}
